import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiUrls } from "../helpers/apiUrls";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import { AGREEMENT_PAYMENT_KINDS, agreementsApiSchema } from "../schemas/agreements";
import { getCalculatedPricingForProduct, getCalculatedPricingForProductDiscounted, getDiscountedPricesForProduct } from "../utils";
import { roundToDecimalPlaces } from "../helpers/methods";

export const agreementsApi = {
    getAgreements: async (axios, params) => {
        return await axios
            .get(apiUrls.agreements.agreements, { params })
            .then((response) => response.data);
            // .then((response) => agreementsApiSchema.getAgreementsResponse.parse(response.data));
    },

    getShortDataAgreements: async (axios, params) => {
        return await axios
            .get(apiUrls.agreements.agreementsShortData, { params })
            .then((response) => response.data);
            // .then((response) => agreementsApiSchema.getAgreementsResponse.parse(response.data));
    },

    getShortDataWithBankingAgreements: async (axios, params) => {
        return await axios
            .get(apiUrls.agreements.agreementsWithBankingShortData, { params })
            .then((response) => response.data);
            // .then((response) => agreementsApiSchema.getAgreementsResponse.parse(response.data));
    },

    getAgreement: async (axios, agreementId) => {
        return axios
            .get(apiUrls.agreements.agreement(agreementId))
            .then((response) => response.data);
    },

    getAgreementDataForNewContact: async (axios, agreementId) => {
        return axios
            .get(apiUrls.agreements.agreementDataForNewContact(agreementId))
            .then((response) => response.data);
    },

    getAgreementRescheduledPaymentList: async (axios, params) => {
        return axios.get(apiUrls.agreements.agreementRescheduledPaymentList, { params }).then((response) => response.data)
    },

    postAgreementRescheduledPaymentList: async (axios, data) => {
        return axios.post(apiUrls.agreements.agreementRescheduledPaymentList, data).then((response) => response.data);
    },

    getAgreementNotificationList: async (axios, agreementId) => {
        return axios
            .get(apiUrls.agreements.agreementNotificationList(agreementId))
            .then((response) => response.data);
    },

    createAgreement: async (axios, data) => {
        return axios.post(apiUrls.agreements.agreements, data).then((response) => response.data);
    },

    updateAgreement: async (axios, agreementId, data) => {
        return axios
            .put(apiUrls.agreements.agreement(agreementId), data)
            .then((response) => response.data);
    },

    partialUpdateAgreement: async (axios, agreementId, data) => {
        return axios
            .patch(apiUrls.agreements.agreement(agreementId), data)
            .then((response) => response.data);
    },

    getPaymentSchedules: async (axios, params) => {
        return await axios
            .get(apiUrls.agreements.paymentSchedules, { params })
            .then((response) =>
                agreementsApiSchema.getPaymentSchedulesResponse.parse(response.data),
            );
    },

    getPaymentSchedule: async (axios, scheduleId) => {
        return axios
            .get(apiUrls.agreements.paymentSchedule(scheduleId))
            .then((response) => agreementsApiSchema.paymentSchedule.parse(response.data));
    },

    createPaymentSchedule: async (axios, data) => {
        return axios
            .post(apiUrls.agreements.paymentSchedules, data)
            .then((response) => agreementsApiSchema.paymentSchedule.parse(response.data));
    },

    updatePaymentSchedule: async (axios, scheduleId, data) => {
        return axios
            .put(apiUrls.agreements.paymentSchedule(scheduleId), data)
            .then((response) => agreementsApiSchema.paymentSchedule.parse(response.data));
    },

    deletePaymentSchedule: async (axios, scheduleId) => {
        return axios.delete(apiUrls.agreements.paymentSchedule(scheduleId));
    },

    getMilestones: async (axios, investmentId) => {
        return axios
            .get(apiUrls.agreements.milestones(investmentId))
            .then((response) => agreementsApiSchema.getMilestonesResponse.parse(response.data));
    },

    getAgreementKinds: async (axios) => {
        return await axios
            .get(apiUrls.agreements.kinds)
            .then((response) => agreementsApiSchema.getAgreementKindsResponse.parse(response.data));
    },

    getAgreementTypes: async (axios) => {
        return await axios
            .get(apiUrls.agreements.types)
            .then((response) => agreementsApiSchema.getAgreementTypesResponse.parse(response.data));
    },
    getAgreementStatuses: async (axios) => {
        return axios
            .get(apiUrls.agreements.statuses)
            .then((response) => agreementsApiSchema.agreementStatusesResponse.parse(response.data));
    },

    getAgreementPayments: async (axios, params) => {
        return await axios
            .get(apiUrls.agreements.payments, { params })
            .then((response) =>
                agreementsApiSchema.getAgreementPaymentsResponse.parse(response.data),
            );
    },

    getDefaultPaymentSchedule: async (axios, investmentId) => {
        return axios
            .get(apiUrls.agreements.defaultPaymentSchedule(investmentId))
            .then((response) =>
                agreementsApiSchema.defaultPaymentScheduleResponse.parse(response.data),
            );
    },

    async getCancellationReasons(axios) {
        return axios
            .get(apiUrls.agreements.cancellationReasons)
            .then((response) =>
                agreementsApiSchema.getAgreementCancellationReasonsResponse.parse(response.data),
            );
    },

    async cancelAgreement(axios, agreementId, reasonId, resonNote, returnReservationPayment) {
        const body = { reason_id: reasonId, status_note: resonNote, return_reservation_payment: returnReservationPayment };
        return axios
            .post(
                apiUrls.agreements.cancel(agreementId),
                agreementsApiSchema.cancelAgreementRequest.parse(body),
            )
            .then((response) => response.data);
    },

    async seFinalSettlementAgreement(axios, agreementId) {
        const body = {};
        return axios
            .post(
                apiUrls.agreements.finalSettlement(agreementId),
                body,
            )
            .then((response) => response.data);
    },

    sendPaymentNotification: async (axios, data) => {
        return axios.post(apiUrls.agreements.notify, data).then((response) => response.data);
    },

    getNotificationSelectList: async (axios, params) => {
        return axios
            .get(apiUrls.agreements.notificationSelectList, { params })
            .then((response) => response.data);
    },

    getAgreementOverduePayments: async (axios, params) => {
        return axios
            .get(apiUrls.agreements.agreementOverduePayments, { params })
            .then((response) => response.data);
    },
};

// React Query hooks

export const useAgreements = (params = new URLSearchParams(), options = {}) => {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["agreements", params.toString()],
        queryFn: () => agreementsApi.getAgreements(axiosPriv, params),
    });
};

export const useShortDataAgreements = (params = new URLSearchParams(), options = {}) => {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["agreements-short-data", params.toString()],
        queryFn: () => agreementsApi.getShortDataAgreements(axiosPriv, params),
    });
};

export const useShortDataWithBankingAgreements = (params = new URLSearchParams(), options = {}) => {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["agreements-short-data-with-banking", params.toString()],
        queryFn: () => agreementsApi.getShortDataWithBankingAgreements(axiosPriv, params),
    });
};

export const useAgreement = (agreementId, options = {}) => {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["agreements", agreementId],
        queryFn: () => agreementsApi.getAgreement(axiosPriv, agreementId),
    });
};

export const useAgreementNotificationList = (agreementId, options = {}) => {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["agreement-notifications", agreementId],
        queryFn: () => agreementsApi.getAgreementNotificationList(axiosPriv, agreementId),
    });
};

export const useCreateAgreement = (options = {}) => {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    return useMutation({
        ...options,
        mutationFn: (data) => agreementsApi.createAgreement(axiosPriv, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["agreements"] });
            await queryClient.invalidateQueries({ queryKey: ["bookings"] });
            options.onSuccess?.(data, variables, context);
        },
    });
};

export const useUpdateAgreement = (options = {}) => {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    return useMutation({
        ...options,
        mutationFn: ({ agreementId, data }) =>
            agreementsApi.updateAgreement(axiosPriv, agreementId, data),
        onSuccess: async (data, variables, context) => {
            console.log(variables, "updateAgreement")
            await queryClient.invalidateQueries({ queryKey: ["agreements"] });
            await queryClient.invalidateQueries({ queryKey: ["agreementPayments"]});
            await queryClient.invalidateQueries({ queryKey: ["agreements", variables?.agreementId] });
            options.onSuccess?.(data, variables, context);
        },
    });
};

export const usePartialUpdateAgreement = (options = {}) => {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    return useMutation({
        ...options,
        mutationFn: ({ agreementId, data }) =>
            agreementsApi.partialUpdateAgreement(axiosPriv, agreementId, data),
        onSuccess: async (data, variables, context) => {
            console.log(variables, "partialUpdateAgreement")
            await queryClient.invalidateQueries({ queryKey: ["agreements"] });
            await queryClient.invalidateQueries({ queryKey: ["agreementPayments"]});
            await queryClient.invalidateQueries({ queryKey: ["agreements", variables?.agreementId] });
            options.onSuccess?.(data, variables, context);
        },
    });
};

export const useAgreementKinds = (options = {}) => {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["agreementKinds"],
        queryFn: () => agreementsApi.getAgreementKinds(axiosPriv),
    });
};

export const useAgreementTypes = (options = {}) => {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["agreementTypes"],
        queryFn: () => agreementsApi.getAgreementTypes(axiosPriv),
    });
};

export const usePaymentSchedules = (params = new URLSearchParams(), options = {}) => {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["paymentSchedules", params.toString()],
        queryFn: () => agreementsApi.getPaymentSchedules(axiosPriv, params),
    });
};

export const usePaymentSchedule = (scheduleId, options = {}) => {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["paymentSchedules", scheduleId],
        queryFn: () => agreementsApi.getPaymentSchedule(axiosPriv, scheduleId),
        enabled: scheduleId != null,
    });
};

export const useCreatePaymentSchedule = (options = {}) => {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    return useMutation({
        ...options,
        mutationFn: (data) => agreementsApi.createPaymentSchedule(axiosPriv, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["paymentSchedules"] });
            options.onSuccess?.(data, variables, context);
        },
    });
};

export const useUpdatePaymentSchedule = (options = {}) => {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    return useMutation({
        ...options,
        mutationFn: ({ scheduleId, data }) =>
            agreementsApi.updatePaymentSchedule(axiosPriv, scheduleId, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["paymentSchedules"] });
            options.onSuccess?.(data, variables, context);
        },
    });
};

export const useDeletePaymentSchedule = (options = {}) => {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    return useMutation({
        ...options,
        mutationFn: ({ scheduleId }) => agreementsApi.deletePaymentSchedule(axiosPriv, scheduleId),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["paymentSchedules"] });
            options.onSuccess?.(data, variables, context);
        },
    });
};

export const useAgreementStatuses = (options = {}) => {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["agreementStatuses"],
        queryFn: () => agreementsApi.getAgreementStatuses(axiosPriv),
    });
};

export const useMilestones = (investmentId, options = {}) => {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["milestones", investmentId],
        queryFn: () => agreementsApi.getMilestones(axiosPriv, investmentId),
    });
};

export const useAgreementPayments = (params = new URLSearchParams(), options = {}) => {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["agreementPayments", params.toString()],
        queryFn: () => agreementsApi.getAgreementPayments(axiosPriv, params),
    });
};

export const usePaymentsForAgreement = (agreementId, options = {}) => {
    const params = new URLSearchParams({"page_size": 100});
    params.set("agreement", agreementId);
    return useAgreementPayments(params, options);
};

export const useDefaultPaymentSchedule = (investmentId, options = {}) => {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["defaultPaymentSchedule", investmentId],
        queryFn: () => agreementsApi.getDefaultPaymentSchedule(axiosPriv, investmentId),
        enabled: investmentId != null,
    });
};

export function useAgreementCancellationReasons(options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        ...options,
        queryKey: ["agreementCancellationReasons"],
        queryFn: () => {
            return agreementsApi.getCancellationReasons(axiosPriv);
        },
    });
}

export function useCancelAgreement(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: ({ agreementId, reasonId, resonNote, returnReservationPayment }) =>
            agreementsApi.cancelAgreement(axiosPriv, agreementId, reasonId, resonNote, returnReservationPayment),
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["agreements"] });
            options.onSuccess?.(data, variables, context);
        },
    });
}

export function useSetFinalSettlementAgreement(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: ({ agreementId}) =>
            agreementsApi.seFinalSettlementAgreement(axiosPriv, agreementId),
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["agreements", data.id]});
            queryClient.invalidateQueries({ queryKey: ["agreements"]});
            options.onSuccess?.(data, variables, context);
        },
    });
}

export function useSendPaymentNotification(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    return useMutation({
        ...options,
        mutationFn: (data) => agreementsApi.sendPaymentNotification(axiosPriv, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["agreements"] });
            await queryClient.invalidateQueries({ queryKey: ["agreementPayments"] });
            options.onSuccess?.(data, variables, context);
        },
    });
}

export const useProductAgreements = (productId, options = {}) => {
    const params = new URLSearchParams();
    params.append("product_id", productId);

    return useAgreements(params, options);
};

export function useNotificationSelectList(params = new URLSearchParams()) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["notification-select_list"],
        queryFn: () => {
            return agreementsApi.getNotificationSelectList(axiosPriv, params);
        },
    });
}

export function useGetAgreementOverduePayments(params = new URLSearchParams()) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["agreement-overdue-payments", params.toString()],
        queryFn: () => {
            return agreementsApi.getAgreementOverduePayments(axiosPriv, params);
        },
    });
}

export function useGetAgreementDetailsForNewContactMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: async ({ agreementId}) =>
            await agreementsApi.getAgreementDataForNewContact(axiosPriv, agreementId),
        onSuccess: (data, variables, context) => {
            options.onSuccess?.(data, variables, context);
        },
    });
}

export function usePostAgreementRescheduledPaymentList(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: async (requestBody) =>
            await agreementsApi.postAgreementRescheduledPaymentList(axiosPriv, requestBody),
        onSuccess: (data, variables, context) => {
            options.onSuccess?.(data, variables, context);
        },
    });
}

export function useGetAgreementRescheduledPaymentList(params = new URLSearchParams()) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["agreement-rescheduled-payments", params.toString()],
        queryFn: () => {
            return agreementsApi.getAgreementRescheduledPaymentList(axiosPriv, params);
        },
    });
}

// Utilities

export const getCustomers = (agreement) => {
    return agreement.shares.flatMap((share) => share.customers);
};

export const calculateAgreementPricing = (agreement) => {
    const productPricings = agreement.products.map((ap) =>
        getCalculatedPricingForProduct(ap.product),
    );

    const totalListedGross = productPricings.reduce(
        (sum, pricing) => sum + pricing.listed.gross,
        0,
    );

    const totalListedNet = productPricings.reduce(
        (sum, pricing) => sum + pricing.listed.net,
        0,
    );

    return {
        totalListedGross,
        totalListedNet,
    };
};

export const calculateAgreementPricingWithDiscount = (agreement) => {
    const productPricings = agreement.products.map((ap) =>
        getCalculatedPricingForProductDiscounted(ap.product, ap.discount_proc),
    );

    const totalListedGross = productPricings.reduce(
        (sum, pricing) => sum + roundToDecimalPlaces(pricing.listed.gross,2),
        0,
    );

    const totalListedNet = productPricings.reduce(
        (sum, pricing) => sum +  roundToDecimalPlaces(pricing.listed.net,2),
        0,
    );

    return {
        totalListedGross,
        totalListedNet,
    };
};

export const isReservationPayment = (payment) => {
    return payment.agrepay_kind === AGREEMENT_PAYMENT_KINDS.Reservation;
};

export const isFinalSettlementPayment = (payment) => {
    return payment.agrepay_kind === AGREEMENT_PAYMENT_KINDS.FinalSettlement;
};

/**
 * amountGross = sum for each product - gross price (including discount) * percentage in this payment
 * totalPercentage = amountGross / sum of amountGross across all payments in the agreement
 */
export const calculateAgreementPaymentPricing = (agreement, payment) => {
    const productPricesIncludingDiscounts = Object.fromEntries(
        agreement.products.map((ap) => [
            ap.product.id,
            getDiscountedPricesForProduct(ap.product, ap.discount_proc),
        ]),
    );

    const totalAmountGross = Object.values(productPricesIncludingDiscounts)
        .map((price) => price.gross)
        .reduce((sum, amount) => sum + amount, 0);

    const totalAmountGrossForThisPayment = payment.per_product_percentages
        .map((pp) => {
            const pricing = productPricesIncludingDiscounts[pp.product_id];
            if (pricing == null) {
                throw new Error(
                    `Payment product ${pp.product_id} not present in agreement ${agreement.id}`,
                );
            }

            return (pricing.gross * Number(pp.percentage)) / 100;
        })
        .reduce((sum, amount) => sum + amount, 0);

    const totalAmountNetForThisPayment = payment.per_product_percentages
        .map((pp) => {
            const pricing = productPricesIncludingDiscounts[pp.product_id];
            if (pricing == null) {
                throw new Error(
                    `Payment product ${pp.product_id} not present in agreement ${agreement.id}`,
                );
            }

            return (pricing.net * Number(pp.percentage)) / 100;
        })
        .reduce((sum, amount) => sum + amount, 0);

    

    const paymentPercentageOfTotalPrice = (totalAmountGrossForThisPayment / totalAmountGross) * 100;

    return {
        amountNet: totalAmountNetForThisPayment,
        amountGross: totalAmountGrossForThisPayment,
        totalPercentage: paymentPercentageOfTotalPrice,
    };
};
